import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { createWithId, getAll, getById, getBySingleQuery, remove, update, updateField } from "../utils/dbUtils";
import { db } from "../firebase";
import { asTimeZone } from "../utils/dateFormatter";
import moment from "moment-timezone";
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { normalizeSurveyFlowItems, validateUniqueSurveyFlowKeys } from "./SurveyFlowItem";

class ProductFamily {
    static collectionName = 'productFamilies';

    id = null;
    name = null;
    slug = null;
    enabled = false;
    allowMultipleActiveSubscriptions = false;
    duplicateKeyFields = [];
    surveys = []; // array of SurveyFlowItem overrides

    constructor(productFamily) {
        if (isObjectNotEmpty(productFamily)) {
            if (isStringNotEmpty(productFamily.id)) { this.id = productFamily.id; }
            if (isStringNotEmpty(productFamily.name)) { this.name = productFamily.name; }
            if (isStringNotEmpty(productFamily.slug)) { this.slug = productFamily.slug; }
            if (isBooleanTrue(productFamily.enabled)) { this.enabled = true; }
            if (isBooleanTrue(productFamily.allowMultipleActiveSubscriptions)) { this.allowMultipleActiveSubscriptions = true; }
            if (isListNotEmpty(productFamily.duplicateKeyFields)) { this.duplicateKeyFields = productFamily.duplicateKeyFields; }
            if (isListNotEmpty(productFamily.surveys)) { this.surveys = normalizeSurveyFlowItems(productFamily.surveys); }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            slug: this.slug,
            enabled: this.enabled,
            allowMultipleActiveSubscriptions: this.allowMultipleActiveSubscriptions,
            duplicateKeyFields: this.duplicateKeyFields,
            surveys: this.surveys,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.id)) {
            throw new Error('Family code is required.');
        }
        if (isStringEmpty(this.name)) {
            throw new Error('Name is required.');
        }
        if (isStringEmpty(this.slug)) {
            throw new Error('Slug is required.');
        }
        validateUniqueSurveyFlowKeys(this.surveys, 'Product family surveys');

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await createWithId(ProductFamily.collectionName, this.id, this.toDto());
        }

        return null;
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(ProductFamily.collectionName, id, fieldName, newValue);
        }

        throw new Error("id is required.");
    }

    static update = async (id, updatedFamily, oldObj) => {
        const updatedObj = new ProductFamily(updatedFamily);
        if (updatedObj.isValid() && id === updatedFamily.id) {
            const success = await update(ProductFamily.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() };
            }
        } else {
            throw new Error('id and id field of the updated product family must match.');
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(ProductFamily.collectionName, id);
        }

        throw new Error("id is required.");
    }

    static getAll = async () => {
        return await getAll(ProductFamily.collectionName);
    }

    static getById = async (id) => {
        return await getById(ProductFamily.collectionName, id);
    }

    static getBySlug = async (slug) => {
        const list = await getBySingleQuery(ProductFamily.collectionName, 'slug', slug, '==');
        return isListNotEmpty(list) ? list[0] : null;
    }

    static getCount = async (queryParameters = {}) => {
        const { start, end } = queryParameters;
        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            const startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            const endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        const q = query(collection(db, ProductFamily.collectionName), ...queryConstraints);
        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters = {}, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { start, end } = queryParameters;
        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            const startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            const endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, ProductFamily.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, ProductFamily.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, ProductFamily.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        const snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
        }));
    }
}

export default ProductFamily;
